/**
 * Blog posts data: loads all .md files from content/blog and provides
 * recommendation logic for the "Recommended Read" block on post pages.
 */
import matter from 'gray-matter'

import { type BlogPostFrontmatter } from '../components/BlogItem'
import { isDraftPost, isHiddenPost } from '../utils/isDraftPost'

const blogContext = require.context('../../../content/blog', false, /\.md$/)

export type BlogPost = {
  slug: string
  fileSlug: string
  frontmatter: BlogPostFrontmatter
}

const normalizeBlogSlug = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined

  const normalizedValue = value.trim().replace(/^\/+|\/+$/g, '')
  return normalizedValue || undefined
}

export const getBlogPostSlug = (frontmatter: BlogPostFrontmatter, fileSlug: string): string =>
  normalizeBlogSlug(frontmatter.slug) ?? fileSlug

/** All blog posts loaded from src/content/blog/*.md (slug + frontmatter). */
export const allBlogPosts: BlogPost[] = blogContext
  .keys()
  .map((key: string) => {
    const fileSlug = key.replace(/^\.\//, '').replace(/\.md$/, '')
    const raw = blogContext(key) as string
    const { data } = matter(raw)
    const frontmatter = data as BlogPostFrontmatter

    return {
      slug: getBlogPostSlug(frontmatter, fileSlug),
      fileSlug,
      frontmatter,
    }
  })
  .filter((post) => !isDraftPost(post.frontmatter.draft) && !isHiddenPost(post.frontmatter.hidden))

/** Normalize frontmatter tags (array of strings or { tag } objects) to string[]. */
function normalizeTags(tags: unknown): string[] {
  if (!Array.isArray(tags)) return []
  return tags
    .map((t) => (typeof t === 'string' ? t : (t as { tag?: string })?.tag))
    .filter(Boolean) as string[]
}

/**
 * Get recommended posts for the "Recommended Read" section.
 *
 * Logic:
 * 1. Excludes the current post by slug.
 * 2. If current post has no tags → return up to `limit` most recent posts (by date).
 * 3. If current post has tags → score other posts by number of matching tags.
 *    - Only posts with at least one matching tag are considered.
 *    - Sort by match count (desc), then by date (newest first).
 *    - If no posts share any tag → fallback to most recent by date.
 * 4. Returns at most `limit` posts.
 *
 * @param currentSlug - Slug of the post we're on (excluded from results).
 * @param currentTags - Tags from current post frontmatter (used for matching).
 * @param limit - Max number of recommendations (default 3).
 * @returns Recommended posts for the block.
 */
export function getRecommendations(
  currentSlug: string,
  currentTags: unknown,
  limit = 3
): BlogPost[] {
  const others = allBlogPosts.filter((p) => p.slug !== currentSlug)
  const normalizedTags = normalizeTags(currentTags)

  if (normalizedTags.length === 0) {
    return others
      .sort((a, b) => {
        const dA = a.frontmatter.date ? new Date(a.frontmatter.date).getTime() : 0
        const dB = b.frontmatter.date ? new Date(b.frontmatter.date).getTime() : 0
        return dB - dA
      })
      .slice(0, limit)
  }

  const withMatchCount = others.map((p) => {
    const pTagList = normalizeTags(p.frontmatter.tags)
    const matches = normalizedTags.filter((t) => pTagList.includes(t)).length
    return { post: p, matches }
  })

  const withMatches = withMatchCount.filter((x) => x.matches > 0)

  if (withMatches.length === 0) {
    return others
      .sort((a, b) => {
        const dA = a.frontmatter.date ? new Date(a.frontmatter.date).getTime() : 0
        const dB = b.frontmatter.date ? new Date(b.frontmatter.date).getTime() : 0
        return dB - dA
      })
      .slice(0, limit)
  }

  return withMatches
    .sort((a, b) => {
      if (b.matches !== a.matches) return b.matches - a.matches
      const dA = a.post.frontmatter.date ? new Date(a.post.frontmatter.date).getTime() : 0
      const dB = b.post.frontmatter.date ? new Date(b.post.frontmatter.date).getTime() : 0
      return dB - dA
    })
    .slice(0, limit)
    .map((x) => x.post)
}
